/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  FormControl,
  FormLabel,
  RadioGroup as MuiRadioGroup,
} from "@mui/material";
import { forwardRef, ReactNode, JSX } from "react";

import { AppTheme } from "@rewards-web/shared/style/theme";
import { ControlledFormFieldProps } from "@rewards-web/shared/types";

import { useFormControlContext } from "../../form/form-control";

interface RadioGroupProps extends ControlledFormFieldProps {
  children?: ReactNode;
  row?: boolean;
  ariaLabel: string;
  label?: string;
  fullWidth?: boolean;
}

export const RadioGroup = forwardRef(
  (
    {
      children,
      label,
      row,
      ariaLabel,
      onChange,
      onBlur,
      name,
      value,
      error,
      fullWidth,
    }: RadioGroupProps,
    ref: any
  ): JSX.Element => {
    const { submitting, readOnly } = useFormControlContext();

    return (
      <FormControl
        variant="standard"
        component="fieldset"
        fullWidth={fullWidth}
        error={!!error}
        aria-invalid={!!error}
        disabled={submitting || readOnly}
      >
        {label && (
          <FormLabel
            css={(theme: AppTheme) => css`
              /* 16px label text, not the app's 18px body1 */
              font-size: 1rem;
              letter-spacing: normal;
              color: ${theme.palette.text.primary};
              padding-bottom: ${theme.spacing(1.5)};
              line-height: 1.3em;
            `}
            component="legend"
          >
            {label}
          </FormLabel>
        )}
        <MuiRadioGroup
          css={(theme: AppTheme) => css`
            flex-wrap: ${row ? "nowrap" : "wrap"};
            ${
              error &&
              css`
                label,
                svg {
                  color: ${theme.palette.error.main};
                }
              `
            }
          `}
          ref={ref}
          name={name}
          onBlur={onBlur}
          value={value}
          onChange={(_, value) => {
            onChange?.(value);
          }}
          aria-label={ariaLabel}
          row={row}
        >
          {children}
        </MuiRadioGroup>
      </FormControl>
    );
  }
);
